import { Region } from 'src/app/core/services/ebr-api-client/ebr-api-client.g';
import { Money } from 'src/app/global/types';
import { EroiMetricComponent } from '../components/eroi-metric/eroi-metric.component';

export type tvdData = {
  valueContribution: string | undefined;
  monetaryContribution: string | undefined;
  modifiedMonetaryContribution?: string | undefined;
  modifiedValueContribution?: string | undefined;
};

/**
 * Typed esponse from the TVD metrics endpoint
 * id: the id of the set of TVD metrics
 * metrics: the set of TVD metrics
 */
export interface TVDMetricGroupResponse {
  id: string;
  brandGroup: string;
  indvMetrics: TvdMetricData[];
  summMetrics: TvdMetricData[];
}

/**
 * Type which holds all information about a TVD Metric group
 * to be consumed by the FE. See distributor TVD component
 */
export interface TvdMetricGroup {
  id: string;
  title: string;
  brandGroup: string;
  groupSelected: boolean;
  indvMetrics: TvdMetricData[];
  summMetrics: TvdMetricData[];
}

/**
 * Respresents a single TVD metric,
 * which can a monetary contribution (ex. $34)
 * and/or a value contribution (ex. 3 gallons)
 */
export type TvdMetricData = {
  $type: 'TvdCostMetric' | 'TvdGrowthMetric';
  id: string;
  brand: string | undefined;
  sortSequenceNumber: number;
  valueContribution: string;
  // TODO: Consider renaming monetaryContribution to originalMonetaryContribution for clarity, as we now have modifiedMonetaryContribution as well. This will help avoid confusion when both values are present.
  // TODO: Enforce imutability of monetaryContribution and only allow mutations on the modifiedMoentaryContribution field to prevent accidental overwrites of the original value.
  monetaryContribution: string | undefined;
  modifiedMonetaryContribution?: string | undefined;
  modifiedValueContribution?: string | undefined;
  currentValue?: number | undefined;
  previousValue?: number | undefined;
};

/**
 * Represents a custom tile created by the user, that can be added to the Powerpoint
 */
export type custTile = {
  title: string;
  cost: string;
  metricValue: string | number;
  unit: string;
  valuePillar: TvdValuePillar;
  impactCategory: TvdImpactCategory;
};

/**
 * Holds all TVD-related data from the FE that is required for PPT generation
 */
export interface TvdPptModel {
  tvdMetrics: TVDMetricGroupResponse[] | undefined; // metrics to display in the ppt, including selected metrics when applicable
  customMetrics: CustomAddedMetric[] | undefined; //custom tiles to display in ppt
}

/**
 * Represents a custom metric added by the user, formatted for PPT download
 */
export type CustomAddedMetric = {
  Title: string; //title
  Value: string; // metric unit value ex. 3
  ValueUnit: string; // the unit for the metric ex. gallons
  Cost: string; // monetary value of the metric ex. 34
  ValuePillar: string; //TvdValuePillar enum without whitespace, ex. "BusinessOutcomes"
  ImpactCategory: string; //ImpactCategory enum without whitespace, ex. "FoodSafety"
};

/**
 * Represents a tile for the Presentation Preview
 */
export type PresentationPreviewTile = {
  title: string;
  iconPath: string;
  valuePillar: TvdValuePillar;
  monetaryContribution: string;
  costCurrencyCode: string;
  valueContribution: string;
  valueUnit: string;
};

export enum TvdDrillDownGrouping {
  Location = 'location',
  Product = 'product',
}

/**
 * Represent the Value Pillars that each Impact Category and each metric falls under.
 */
export enum TvdValuePillar {
  BusinessOutcomes = 'Business Outcomes',
  OperationalPerformance = 'Operational Performance',
  EnvironmentalImpact = 'Environmental Impact',
}

/**
 * Impact Categories for TVD metrics, each individual metric maps to one of these categories.
 * And each Impact Category is a part of a Value Pillar.
 */
export enum TvdImpactCategory {
  //Business Outcomes
  FoodSafety = 'Food Safety',
  HumanSafety = 'Human Safety',
  WaterSafety = 'Water Safety',
  Brand = 'Brand',
  DelightedGuests = 'Delighted Guests',
  Quality = 'Quality',
  //Operational Performance
  RegulatoryCompliance = 'Regulatory Compliance',
  IndustryCompliance = 'Industry Compliance',
  LaborProductivity = 'Labor Productivity',
  LocationProductivity = 'Location Productivity',
  ProductOptimization = 'Product Optimization',
  RawMaterialOptimization = 'Raw Material Optimization',
  Capacity = 'Capacity',
  AssetProtection = 'Asset Protection',
  Reliability = 'Reliability',
  //Environmental Impact
  Water = 'Water',
  ElectricalEnergy = 'Electrical Energy',
  ThermalEnergy = 'Thermal Energy',
  GreenhouseGases = 'Greenhouse Gases',
  SolidWaste = 'Solid Waste',
  LiquidWaste = 'Liquid Waste',
  AirEmissions = 'Air Emissions',
}

/**
 * Mapping of Impact Categories to their corresponding Value Pillars.
 */
export const ValuePillarToImpactCategoryMapping = new Map<
  TvdValuePillar,
  TvdImpactCategory[]
>([
  [
    TvdValuePillar.BusinessOutcomes,
    [
      TvdImpactCategory.FoodSafety,
      TvdImpactCategory.HumanSafety,
      TvdImpactCategory.WaterSafety,
      TvdImpactCategory.Brand,
      TvdImpactCategory.DelightedGuests,
      TvdImpactCategory.Quality,
    ],
  ],
  [
    TvdValuePillar.OperationalPerformance,
    [
      TvdImpactCategory.RegulatoryCompliance,
      TvdImpactCategory.IndustryCompliance,
      TvdImpactCategory.LaborProductivity,
      TvdImpactCategory.LocationProductivity,
      TvdImpactCategory.ProductOptimization,
      TvdImpactCategory.RawMaterialOptimization,
      TvdImpactCategory.Capacity,
      TvdImpactCategory.AssetProtection,
      TvdImpactCategory.Reliability,
    ],
  ],
  [
    TvdValuePillar.EnvironmentalImpact,
    [
      TvdImpactCategory.Water,
      TvdImpactCategory.ElectricalEnergy,
      TvdImpactCategory.ThermalEnergy,
      TvdImpactCategory.GreenhouseGases,
      TvdImpactCategory.SolidWaste,
      TvdImpactCategory.LiquidWaste,
      TvdImpactCategory.AirEmissions,
    ],
  ],
]);

export enum eroiIds {
  // Product TVD metrics
  // TODO : Understand implications of adding REPAIR and REPLACEMENT as enums
  WATER = 'water',
  ENERGY = 'energy',
  GREENHOUSEGASES = 'greenhouse_gases', // Combined metric for greenhouse gases, includes energy, transportation and packaging related greenhouse gas reductions
  GREENHOUSEGASES_ENERGY = 'greenhouse_gases_energy',
  GREENHOUSEGASES_TRANSPORT = 'greenhouse_gases_transport',
  GREENHOUSEGASES_PACKAGING = 'greenhouse_gases_packaging',
  PLASTIC = 'plastic_waste',
  CARDBOARD = 'cardboard_waste',
  SOLID_WASTE = 'solid_waste', // Combined metric for solid waste reduction, includes both plastic and cardboard waste reduction
  EMPLOYEESAFETY = 'employee_safety',
  PRODUCTIVITY = 'productivity',
  ASSETS = 'assets',
  COSTAVOIDANCE = 'cost_avoidance',
  PORTFOLIO = 'portfolio_reduction',
  CUSTOMTILE = 'custom_tile',

  TOTALVALUEDELIVERED = 'tvd_overall',
  TOTALSALES = 'total_sales',

  //Distributor Business Metrics
  CASEGROWTH = 'case_growth',
  TOTALCUSTOMERS = 'total_customers',
  SALESGROWTH = 'sales_growth',
  CUSTOMERGROWTH = 'customer_growth',
  TOTALCASECOUNT = 'total_case_count',
  TOTALSALESDOLLARS = 'total_sales_dollars',
  DELIVEREDINFULLONTIME = 'delivered_in_full_on_time',
  CASEFILLRATE = 'case_fill_rate',
  FULLPALLET = 'full_pallet',
  AVERAGEORDERSIZE = 'average_order_size',


  // Service TVD metrics
  TOTALSERVICES = 'total_services',
  NOCHARGEADDITIONALSERVICES = 'no_charge_additional_services',
  GENERALCONTRACTORCOSTAVOIDANCE = 'general_contractor_cost_avoidance',
  SETUPSERVICES = 'setup_services',
  PESTPREVENTIONFINDINGS = 'pest_prevention_findings',
  ROOMNIGHTSSAVED = 'room_nights_saved',
  FURNITUREPRESERVATIONASSETS = 'furniture_preservation_assets',
  FURNITUREPRESERVATIONENERGY = 'furniture_preservation_energy',
  LABORPRODUCTIVITY = 'labor_productivity',
}

/**
 * Number of decimal places to display for each metric's value contribution.
 * Defaults to 0 for any metric not listed here.
 * To add decimal formatting for a new metric, simply add an entry below.
 */
export const metricValueDecimalPlaces: Partial<Record<eroiIds, number>> = {
  [eroiIds.CASEFILLRATE]: 1,
  [eroiIds.DELIVEREDINFULLONTIME]: 1,
  [eroiIds.CASEGROWTH]: 1,
  [eroiIds.SALESGROWTH]: 1,
  [eroiIds.CUSTOMERGROWTH]: 1,
  [eroiIds.FULLPALLET]: 1,
  [eroiIds.AVERAGEORDERSIZE]: 1,
};

export enum tvdGroupIds {
  DEFAULT = 'default_tvd',
  DISTRIBUTOR = 'dist_business',
  SMARTPOWER = 'smartpower',
  AQUANOMIC = 'aquanomic',
  PEST = 'pest_tvd',
}

export enum availablePrograms {
  ALL = 'All',
  AQUANOMIC = 'Aquanomic 2.0',
  SMARTPOWER = 'SMARTPOWER',
}

export enum availableBrandGroups {
  ALL = 'All',
  ECOLAB = 'Ecolab',
  DISTRIBUTOR = 'Distributor',
}

export type TVDProductDetails = {
  metricType: string;
  metricUnit: string;
  totalLocations: string;
  totalContributingProducts: string;
  avgTVDMetricQuantity: string;
  avgTVDMetricDollars: string;
  modifiedAvgTVDMetricQuantity?: string;
  productRows: TVDProductDetailsRow[];
};

export type TVDProductDetailsRow = {
  productCode: string;
  productName: string;
  productDesc: string;
  solution: string;
  numberOfLocations: number;
  isEDPRental: boolean;
  quantityPurchased: number;
  estimatedMetricDollars: number;
  modifiedEstimatedMetricDollars: number;
  estimatedMetricQuantity: number;
  percentOfTotal: number;
  measurementCategory: 'Liquid' | 'Solid' | 'Gas';
};

export type TVDLocationDetailsRowData = {
  siteID: string;
  siteName: string;
  streetAddress: string;
  city: string;
  state: string | null;
  zipCode: string;
  contributingProducts: number;
  estimatedMetricDollars: number;
  modifiedEstimatedMetricDollars: number;
  estimatedMetricQuantity: number;
  percentOfTotal: number;
  h5Number: string;
  h5Name: string;
  h7Number: string;
  h7Name: string;
  countryCode: string;
  trName: string;
  amName: string;
  dmName: string;
};

export type TVDLocationDetails = {
  metricType: string;
  metricUnit: string;
  totalLocations: string;
  totalContributingProducts: string;
  avgTVDMetricQuantity: string;
  avgTVDMetricDollars: string;
  locationRows: TVDLocationDetailsRowData[];
  modifiedAvgTVDMetricQuantity?: string;
};

export type TVDMetricDrilldownDetails = {
  /**
   * @deprecated
   */
  metricUnits: string;
  tvdDollars: number;
  tvdMetric: number;
  /**
   * @deprecated
   */
  metricIcon: string;
  defaultTVDDollars: number;
  defaultTVDMetric: number;
};

//region Pest TVD types
export type TvdLocationServicesDetails = {
  metricType: string;
  metricUnit: string;
  totalLocations: string;
  totalServices: string;
  avgPerServiceCost: string;
  secondaryMetricSum?: string | undefined;
  locationRows: TVDLocationServicesDetailsRowData[];
  modifiedAvgPerServiceCost?: string;
};

export type TVDLocationServicesDetailsRowData = {
  serviceAccountId: string;
  serviceAccountName: string;
  streetAddress: string;
  city: string;
  state: string | null;
  zipCode: string;
  estimatedMetricDollars: number;
  modifiedEstimatedMetricDollars: number;
  estimatedMetricQuantity: number;
  percentOfTotal: number;
  estimatedSecondaryMetricQuantity?: number;

  //todo replace
  // h5Number: string,
  // h5Name: string,
  // h7Number: string,
  // h7Name: string,
};

//endregion

export type TVDTableHeader = {
  title: string | null;
  sortBy:
    | keyof TVDProductDetailsRow
    | keyof TVDLocationDetailsRowData
    | keyof TVDLocationServicesDetailsRowData;
  neptuneCssClass: string;
  sortDirection: 'asc' | 'desc';
};

export interface TwoFactorMetric {
  id?: string;
  title: string;
  isHidden: boolean;
  isCurrencyVisible?: boolean;
  isDefaultValueVisible?: boolean;
  staticUOM?: string;
  isDecimal?: boolean;
}

export interface Metric {
  id: string;
  title: string;
  isHidden: boolean;
  isIncludePayload: boolean;
  isTwoFactorsApplicable?: boolean;
  twoFactorMetrics?: TwoFactorMetric[];
}

// TODO: Rename MetricConfig to MetricDisplayConfig or something similar to avoid confusion with the Metric interface which represents an individual metric
export interface MetricConfig {
  metricName: string;
  metricSubHeader: string;
  metrics: Metric[];
  hasVisibleMetrics?: boolean; // Indicates if there are any visible metrics in this config
  hideSubHeader?: boolean; // Indicates if the subheader should be hidden
}

export interface EroiMetricParams {
  waterComponentData: EroiMetricComponent;
  energyComponentData: EroiMetricComponent;
  productivityComponentData: EroiMetricComponent;
}

export interface ModifiedFormData {
  [key: string]: number | string; // Adjust the types based on your form data structure
}

// TODO: Review the usage of ModalActions enum and consider renaming it to something more specific to its purpose in the TVD context, such as TvdEditAction or TvdModalAction, to improve code readability and maintainability.
// This is used in the TvdDialogComponent to determine whether the user wants to save changes,
// close the dialog without saving, or preview the changes before saving. Depending on the action,
// different logic is executed in the component. For example, if the action is SAVE, the changes
// are saved and the dialog is closed. If the action is CLOSE, the dialog is simply closed without
// saving any changes. If the action is PREVIEW, a preview of the changes is shown to the user
// without actually saving them, allowing the user to review their modifications before deciding
// to save or discard them.
export enum ModalActions {
  SAVE = 'SaveAndClose',
  CLOSE = 'Closed',
  PREVIEW = 'Preview',
}

export interface TVDRateDetail {
  id: string;
  category: string;
  rate: number;
  uom: string; //currently only used for Pest division cost assumptions. INST, QSR, FRS are hardcoded
  country: string;
}

export interface TVDRateDetailsResponse {
  regions: string[];
  division: string;
  tvdRateDetails: TVDRateDetail[];
}

// For standard tiles
export const StandardTileMap: { [key: string]: string } = {
  water: 'Water',
  energy: 'Energy',
  'greenhouse-gases': 'Greenhouse Gases',
  'plastic-waste': 'Plastic Waste',
  productivity: 'Productivity',
  assets: 'Assets',
  'cardboard-waste': 'Cardboard Waste',
  'employee-safety': 'Employee Safety',
  'cost-avoidance': 'Cost Avoidance',
  'portfolio-reduction': 'Portfolio Reduction',
  'custom-tile': '',
};

// For Pest division tiles
export const PestTileMap: { [key: string]: string } = {
  no_charge_additional_services: 'No Charge Additional Services',
  general_contractor_cost_avoidance: 'General Contractor Cost Avoidance',
  setup_services: 'Set-up Services',
  pest_prevention_findings: 'Pest Prevention Findings',
  room_nights_saved: 'Room Nights Saved',
  furniture_preservation_assets: 'Furniture Preservation (Assets)',
  furniture_preservation_energy: 'Furniture Preservation (Energy)',
  'custom-tile': '',
};

export interface PestTVDMetricsData {
  furniturePreservationAssetsComponent?: EroiMetricComponent | undefined;
  furniturePreservationEnergyComponent?: EroiMetricComponent | undefined;
  noChargeAdditionalServicesComponent?: EroiMetricComponent | undefined;
  generalContractorCostAvoidanceComponent?: EroiMetricComponent | undefined;
  setupServicesComponent?: EroiMetricComponent | undefined;
  pestPreventionFindingsComponent?: EroiMetricComponent | undefined;
  roomNightsSavedComponent?: EroiMetricComponent | undefined;
}

export interface TvdLocationAdvancedFilter {
  siteNumber?: string[];
  siteName?: string[];
  country?: string[];
  state?: string[];
  city?: string[];
  zipCode?: string[];
}

export interface TvdProductAdvancedFilter {
  productGroup?: string[];
  productName?: string[];
  productSku?: string[];
}

export type TvdAdvancedFilterOption = {
  siteNumber: string;
  siteName: string;
  country: string;
  state: string;
  city: string;
  zipCode: string;
  productGroup: string;
  productName: string;
  productSku: string;
};

export type SelectedTvdAdvancedFilters = {
  siteNumber: string[];
  siteName: string[];
  country: string[];
  state: string[];
  city: string[];
  zipCode: string[];
  productGroup: string[];
  productName: string[];
  productSku: string[];
};

export type TvdFilterKey =
  | 'siteNumber'
  | 'siteName'
  | 'country'
  | 'state'
  | 'city'
  | 'zipCode'
  | 'productGroup'
  | 'productName'
  | 'productSku';

export const componentConfigAndMetricsIdsMap: Map<string, string[]> = new Map([
  [eroiIds.GENERALCONTRACTORCOSTAVOIDANCE, ['generalcontractorcostavoidance']],
  [eroiIds.NOCHARGEADDITIONALSERVICES, ['valueofservice','rodentservicemultiplier']],
  [eroiIds.SETUPSERVICES, ['valueofservice', 'setupservicescomultiplier']],
  [eroiIds.PESTPREVENTIONFINDINGS, ['preventionfindings']],
  [eroiIds.FURNITUREPRESERVATIONASSETS, ['furniturepreservationassets']],
  [eroiIds.FURNITUREPRESERVATIONENERGY, ['furniturepreservationenergy']],
  [eroiIds.ROOMNIGHTSSAVED, ['roomprice', 'numberofroomnights']],
  [eroiIds.ENERGY, ['energy']],
  [eroiIds.WATER, ['water']],
  //Note: For the QSR and FRS divisions, we use only productivity value alone
  // Might need to update methods using this const accordingly when drilldown is implemented
  [eroiIds.PRODUCTIVITY, ['hospitality', 'foodservice', 'productivity']],
]);

export const PestMetricIds: string[] = [
  eroiIds.NOCHARGEADDITIONALSERVICES,
  eroiIds.GENERALCONTRACTORCOSTAVOIDANCE,
  eroiIds.SETUPSERVICES,
  eroiIds.PESTPREVENTIONFINDINGS,
  eroiIds.ROOMNIGHTSSAVED,
  eroiIds.FURNITUREPRESERVATIONASSETS,
  eroiIds.FURNITUREPRESERVATIONENERGY,
];

export const InsitutionalMetricIds: string[] = [
  eroiIds.ENERGY,
  eroiIds.WATER,
  eroiIds.PRODUCTIVITY,
];

export interface InteractionContext {
  type: string;
  metricName: string;
}

export const MetricNamesMap: Record<string, string> = {
  generalcontractorcostavoidance: 'General Contractor Cost Avoidance',
  nochargeadditionalcomultiplier: 'No Charge Additional Co Multiplier',
  valueofservice: 'Value Of Service',
  setupservicescomultiplier: 'Set-up Services Co Multiplier',
  preventionfindings: 'Prevention Findings',
  furniturepreservationassets: 'Furniture Preservation Assets',
  furniturepreservationenergy: 'Furniture Preservation Energy',
  roomprice: 'Room Price',
  numberofroomnights: 'Number Of Room Nights',
  energy: 'Energy',
  water: 'Water',
  hospitality: 'Hospitality',
  foodservice: 'Foodservice',
  productivity: 'Productivity',
};

export const BrandGroupMap: { [key: string]: string } = {
  All: 'All',
  Ecolab: 'Ecolab Core',
  Distributor: 'Distributor Brands',
};

/**
 * Per-pillar list of eroiIds used for counting the number of visible tiles per pillar.
 * Unlike TvdValuePillarToEroiIdMapping (which omits greenhouse-gas sub-metrics to avoid
 * double-counting in value calculations), this mapping includes every tile shown in the UI
 * so the presentation slide limit can be validated accurately.
 */
export const TvdValuePillarToTileIds: Record<TvdValuePillar, string[]> = {
  [TvdValuePillar.BusinessOutcomes]: [
    eroiIds.EMPLOYEESAFETY,
  ],
  [TvdValuePillar.OperationalPerformance]: [
    // Product
    eroiIds.PRODUCTIVITY, eroiIds.ASSETS, eroiIds.COSTAVOIDANCE, eroiIds.PORTFOLIO,
    // Pest
    eroiIds.NOCHARGEADDITIONALSERVICES, eroiIds.GENERALCONTRACTORCOSTAVOIDANCE,
    eroiIds.SETUPSERVICES, eroiIds.PESTPREVENTIONFINDINGS,
    eroiIds.ROOMNIGHTSSAVED, eroiIds.FURNITUREPRESERVATIONASSETS,
  ],
  [TvdValuePillar.EnvironmentalImpact]: [
    eroiIds.WATER, eroiIds.ENERGY,
    // Greenhouse gases are shown as four separate tiles in the UI
    eroiIds.GREENHOUSEGASES, eroiIds.GREENHOUSEGASES_ENERGY,
    eroiIds.GREENHOUSEGASES_TRANSPORT, eroiIds.GREENHOUSEGASES_PACKAGING,
    eroiIds.PLASTIC, eroiIds.CARDBOARD,
    // Pest
    eroiIds.FURNITUREPRESERVATIONENERGY,
  ],
};

/**
 * Mapping each default eroi metric to the value pillar they belong to for Value Pillar contribution calculations.
 */
export const TvdValuePillarToEroiIdMapping: Map<TvdValuePillar, string[]> =
  new Map([
    [TvdValuePillar.BusinessOutcomes, [eroiIds.EMPLOYEESAFETY]],
    [
      TvdValuePillar.OperationalPerformance,
      [
        // Product
        eroiIds.PRODUCTIVITY,
        eroiIds.ASSETS,
        eroiIds.COSTAVOIDANCE,
        eroiIds.PORTFOLIO,
        // Pest
        eroiIds.NOCHARGEADDITIONALSERVICES,
        eroiIds.GENERALCONTRACTORCOSTAVOIDANCE,
        eroiIds.SETUPSERVICES,
        eroiIds.PESTPREVENTIONFINDINGS,
        eroiIds.ROOMNIGHTSSAVED,
        eroiIds.FURNITUREPRESERVATIONASSETS,
      ],
    ],
    [
      TvdValuePillar.EnvironmentalImpact,
      [
        eroiIds.WATER,
        eroiIds.ENERGY,
        eroiIds.GREENHOUSEGASES, // Not including the breakdown metrics (energy, transportation, packaging) to avoid double counting
        // For solid waste calculation use the individual components to avoid any rounding that might have been introduced in api calculations of solid waste
        eroiIds.PLASTIC,
        eroiIds.CARDBOARD,
        // Pest
        eroiIds.FURNITUREPRESERVATIONENERGY,
      ],
    ],
  ]);

/**
 * Reverse mapping of the TvdValuePillarToEroiIdMapping for easy lookup of value pillar by metric id
 */
export const EroiIdToPillarMapping = (() => {
  const reverse = new Map<string, TvdValuePillar>();
  for (const [pillar, ids] of TvdValuePillarToEroiIdMapping) {
    for (const eroId of ids) {
      reverse.set(eroId, pillar);
    }
  }
  return reverse;
})();

/**
 * A map for non pest and distributor division programs to groupIds for the TVD response
 */
export const TvdProgramToGroupIdMapping: Map<string, tvdGroupIds> = new Map([
  [availablePrograms.ALL, tvdGroupIds.DEFAULT],
  [availablePrograms.AQUANOMIC, tvdGroupIds.AQUANOMIC],
  [availablePrograms.SMARTPOWER, tvdGroupIds.SMARTPOWER],
]);

export interface TvdDrillInMetricTiles {
  //Id is optional filed, that is used for Automation purpose to uniquely identify.
  id?: string;
  title: string;
  originalValue?: number | string | null;
  showDefaultValue?: boolean;
  modifiedValue: number | string | null; // TODO: Should be renamed to currentValue?
  isCurrency?: boolean;
  // TODO; Consider renaming this to
  /**
   * @deprecated Use isModifiedValueHighlighted instead for evaluating which it should
   * use the CSS class for highlighting the modified values  in the template. This
   * eliminates to need to hard-code CSS class names in the code, which would make it
   * easier to locate the CSS class selectors since they are not hard-coded in the code
   * and reduces the risk of inconsistencies between the code and the CSS.
   */ 
  previewCssClass?: string;
  isModifiedValueHighlighted: boolean;
}

//#region TVD By Programs
export interface TvdByProgramData {
  program: string;
  programMetrics: TvdProgramRegionMetrics[];
}

export interface TvdProgramRegionMetrics {
  region: Region;
  totalProducts: number;
  totalLocations: number;
  totalValueDelivered: Money;
  impactCategories: TvdImpactCategory[];
}

export interface TvdByProgramAggregatedRow {
  program: string;
  totalValueDelivered: Money;
  totalProducts: number;
  totalLocations: number;
  impactCategories: TvdImpactCategory[];
}

export interface TvdByProgramDetailsData {
  program: string;
  programDetailsByRegion: TvdProgramRegionDetails[];
}

export interface TvdProgramRegionDetails {
  region: Region;
  programDataByProduct: TvdProgramProductDetails[];
}

export interface TvdProgramProductDetails {
  program: string;
  productCode: string;
  productName: string;
  totalValueDelivered: Money;
  impactCategories: TvdImpactCategory[];
  locations: TvdProgramLocationDetails[];
}

export interface TvdProgramLocationDetails {
  siteId: string;
  siteName: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
  region: Region;

  productName: string;
  productCode: string;
  totalValueDelivered: Money;
  casesPurchased: number;
  impactCategories: TvdImpactCategory[];
}

export enum TvdProgramDrillDownGrouping {
  Product = 'Product',
  Location = 'Location',
}

export interface TvdProgramTableFilterState {
  programSearchTerm: string;
  selectedRegions: Region[];
  selectedImpactCategories: TvdImpactCategory[];
  pageIndex: number;
  pageSize: number;
}
//#endregion

export type CostAssumptionsLayout = 'flat' | 'byCountry';

export interface CountryDisplayConfig {
  countryCode: string;
  countryName: string;
  expanded?: boolean;
  metricsDisplayConfig: MetricConfig[];
}

// TODO: Consider making this configurable via Azure App Configuration or a similar mechanism if the list of regions is expected to change frequently, otherwise this hardcoded list is fine for now.
export const REGIONS_SUPPORTING_COUNTRY_SCOPED_COST_ASSUMPTIONS = new Set<string>([
  'AP',
  'LA',
  'IMEA',
]);

export interface TvdMetricDisplayData {
  currentMonetaryContribution: string,
  defaultMonetaryContribution?: string,
  currentValueContribution: string,
  defaultValueContribution?: string,
  isMonetaryContributionModified: boolean,
  isValueContributionModified: boolean,
}
